export type HexAddress = `0x${string}`;

/**
 * Validates that a string is a well-formed Ethereum hex address (0x + 40 hex chars).
 * Pure validator + type-narrower — does NOT canonicalize / lowercase.
 * Canonicalization (getAddress().toLowerCase()) happens at hook boundary in useEffectiveAddress.
 *
 * Throws on: missing 0x prefix, wrong length, non-hex characters.
 */
export function assertHexAddress(value: string): HexAddress {
  if (!value.startsWith("0x")) {
    throw new Error("assertHexAddress: missing 0x prefix");
  }
  if (value.length !== 42) {
    throw new Error("assertHexAddress: wrong length");
  }
  if (!/^0x[0-9a-fA-F]{40}$/.test(value)) {
    throw new Error("assertHexAddress: non-hex chars");
  }
  // validator returns narrowed type — assertion safe per runtime checks above
  return value as HexAddress;
}

/**
 * Boolean type-guard for Ethereum hex addresses (0x + 40 hex chars).
 * Narrows `value` to `HexAddress` (`0x${string}`) — does NOT throw.
 * Use at hook entry to gate chain reads without casting (G2-1 r6).
 */
export function isHexAddress(value: string): value is HexAddress {
  return /^0x[0-9a-fA-F]{40}$/.test(value);
}
